// RS256 verification of the broker JWT against the published JWKS. Uses `jose`,
// which runs on the Workers runtime (Web Crypto). The remote key set is cached
// per origin for the isolate's lifetime — jose refetches only on an unknown
// `kid`, i.e. broker key rotation — so there is no KV/`caches.default`
// dependency. Failures are values, not throws.
//
// Deliberately free of `ConsumerAppConfig`: this module is the whole broker-JWT
// trust boundary in one importable piece, so a Convex action or any other
// non-worker verifier can reuse it without inventing a consumer app.

import { classifyJoseError } from "@bindery/jose-kit/errors";
import {
  createRemoteJWKSet,
  jwtVerify,
  type JWTVerifyGetKey,
  type CryptoKey,
  type KeyObject,
} from "jose";
import {
  ReaderSessionPayloadSchema,
  UserSessionPayloadSchema,
  type ReaderSessionPayload,
  type UserSessionPayload,
} from "@bindery/shared/contracts/session";

export { classifyJoseError };

/** A verification key: a remote JWKS resolver or a concrete public key (tests). */
export type VerifyKey = JWTVerifyGetKey | CryptoKey | KeyObject | Uint8Array;

/**
 * A broker session type a consumer can accept — the contract's `typ`
 * discriminator. `user_session` is a person signed in with a full account;
 * `reader_session` is a reader with no sign-in of their own, minted by the
 * broker's reader lanes (`/token`, `/reader-session`, `/claim-reader`, the
 * light Google lane) with no credential required on `/reader-session`.
 */
export type SessionLane = "user_session" | "reader_session";

/** Every lane, for validating an app's declaration at runtime. */
export const SESSION_LANES: readonly SessionLane[] = [
  "user_session",
  "reader_session",
];

/**
 * The lanes a consumer accepts when it declares none: the full-account lane
 * only. Every consumer that predates the reader lane relies on this default,
 * and a `reader_session` reaching one of them is `wrong_typ`, exactly as
 * before the lane existed.
 */
export const DEFAULT_LANES: readonly ["user_session"] = ["user_session"];

/** Whether `value` names a lane this package knows. */
export function isSessionLane(value: unknown): value is SessionLane {
  return value === "user_session" || value === "reader_session";
}

export interface VerifyOptions<L extends SessionLane = "user_session"> {
  issuer: string;
  audience: string;
  /**
   * The `typ` values this verification accepts. Omitted, it is
   * {@link DEFAULT_LANES} — `user_session` only — and the result type says
   * so. A token whose `typ` is not listed is `wrong_typ`.
   */
  lanes?: readonly L[];
}

/**
 * Claims a consumer may use — opaque ids, the platform role and the
 * `readerNames` roster, whose nicknames are public display names, never PII.
 *
 * A `Pick` of the contract's `user_session` arm rather than a hand-written
 * shape, so the field types cannot drift from what the broker mints. It is
 * picked key by key deliberately: the arm also carries a deprecated
 * `firstName`, and widening this type wholesale is how a name reaches a
 * consumer's logs. Correlate on `sub` or `readerId`; a consumer that needs a
 * person's name asks the API for it.
 */
export type SessionClaims = Pick<
  UserSessionPayload,
  "sub" | "readerId" | "internalRole" | "readerNames"
>;

/**
 * Claims a consumer may use from a `reader_session` — the same two opaque ids
 * as {@link SessionClaims}, from the contract's reader arm, where `readerId`
 * is required and `sub` equals it (the broker's `signSessionData` sets one
 * from the other). The arm's `readerNickname` is a public display name, not
 * PII, but it is left out for the reason `SessionClaims` is a two-key `Pick`:
 * the claims handed on are ids, and a name is the API's to answer. `linked`
 * is only whether some account is linked, never which one. `method` is the
 * credential the broker verified at the mint; a consumer gating paid work on
 * it must treat an absent one as `anonymous`.
 */
export type ReaderSessionClaims = Pick<
  ReaderSessionPayload,
  "readerId" | "linked" | "method"
> & {
  sub: string;
};

/**
 * A verified session, discriminated on `lane` — the token's own `typ`. A
 * caller that accepts both lanes MUST branch on `lane` before it reads the
 * claims: a `reader_session`'s `sub` is a readerId UUID, not a WorkOS id, and
 * keying anything account-shaped (an entitlement, an admin row, a quota) off
 * it is how a reader passes as a signed-in user.
 */
export type VerifiedSession =
  | {
      lane: "user_session";
      jwt: string;
      exp: number;
      /** The signed `iat`, when the token carries one. */
      iat?: number;
      claims: SessionClaims;
    }
  | {
      lane: "reader_session";
      jwt: string;
      exp: number;
      /** The signed `iat`, when the token carries one. */
      iat?: number;
      claims: ReaderSessionClaims;
    };

/**
 * The claims this verifier judges: the contract's `user_session` arm `.pick`ed
 * to the discriminator, the arm's one required field, and the two opaque ids
 * `SessionClaims` carries out.
 *
 * A pick, not the whole arm, on purpose. Parsing the entire arm made the
 * verifier refuse a signed, unexpired, correctly-issued token over a claim it
 * never consumes — an org role outside the contract's enum, a multi-valued
 * `aud` that jose accepts and `z.string()` does not — and the broker that
 * mints those lives in another repository. A consumer must not sign a reader
 * out over a field it does not use; `internalRole` and `readerNames`, which it
 * does hand on, are parsed apart and dropped when malformed for the same
 * reason. What the pick keeps: a `readerId` that is not a UUID, or a missing
 * `workosId`, is still `malformed_claims`, because those are the claims this
 * function hands on.
 */
const ConsumedClaimsSchema = UserSessionPayloadSchema.pick({
  typ: true,
  workosId: true,
  sub: true,
  readerId: true,
});

/**
 * The reader arm's counterpart of {@link ConsumedClaimsSchema}, picked for the
 * same reason: `readerId` (required, a UUID) and `sub`, nothing a consumer
 * does not hand on. `sub` is required here where the user pick leaves it
 * optional, and must equal `readerId` (checked after the parse): a reader
 * token is keyed by exactly one id, and one whose two ids disagree was not
 * minted by the broker this package trusts.
 */
const ConsumedReaderClaimsSchema = ReaderSessionPayloadSchema.pick({
  typ: true,
  readerId: true,
  sub: true,
  linked: true,
}).required({ sub: true });

// Parsed apart from ConsumedClaimsSchema so a role outside the contract's
// enum is dropped rather than refusing the token: a broker in another
// repository may mint a role this consumer predates, and that must cost the
// user the role, not their session.
const RoleClaimSchema = UserSessionPayloadSchema.pick({ internalRole: true });

// Dropped rather than refused for RoleClaimSchema's reason: a malformed
// roster costs the adult the reader picker, not their session.
const RosterClaimSchema = UserSessionPayloadSchema.pick({ readerNames: true });

// Dropped rather than refused for RoleClaimSchema's reason; absent is the
// least a reader can hold, so a dropped method grants nothing.
const ReaderMethodClaimSchema = ReaderSessionPayloadSchema.pick({
  method: true,
});

export type VerifyResult<L extends SessionLane = "user_session"> =
  | ({ ok: true } & Extract<VerifiedSession, { lane: L }>)
  | { ok: false; reason: string };

/** A hung JWKS host must not hold a sign-in or a session read open. */
export const JWKS_TIMEOUT_MS = 5_000;

const jwksCache = new Map<string, JWTVerifyGetKey>();

/**
 * Remote JWKS resolver for `${brokerOrigin}/.well-known/jwks.json`, cached per
 * origin for the isolate's lifetime.
 */
export function getRemoteJwks(brokerOrigin: string): JWTVerifyGetKey {
  let jwks = jwksCache.get(brokerOrigin);
  if (!jwks) {
    jwks = createRemoteJWKSet(
      new URL(`${brokerOrigin}/.well-known/jwks.json`),
      {
        timeoutDuration: JWKS_TIMEOUT_MS,
      },
    );
    jwksCache.set(brokerOrigin, jwks);
  }
  return jwks;
}

/**
 * Verify a broker JWT: RS256 signature via `key`, plus `iss`, `aud`, `exp`, and
 * `typ` ∈ `options.lanes` (default `user_session` only — the consumer
 * obligation from the broker's `docs/auth-api-reference.md`), and finally the
 * claims this verifier READS against a `.pick` of the matching contract arm,
 * so the field types are the broker's own while a claim nobody here reads
 * cannot refuse the token. Returns the verified token, its `exp`, its `lane`
 * and the opaque session claims on success; a typed, case-distinguishing
 * failure otherwise (`signature`, `issuer`, `audience`, `expired`,
 * `no_matching_key`, `jwks_timeout`, `jwks_fetch_failed`, `no_exp`,
 * `wrong_typ`, `malformed_claims`).
 *
 * The `typ` check runs before the parse so a genuine token of a lane the
 * caller did not accept still reports `wrong_typ` — the case a consumer wants
 * distinguished — rather than collapsing into `malformed_claims` because it
 * failed the other arm.
 */
export async function verifyBrokerJwt<L extends SessionLane = "user_session">(
  jwt: string,
  key: VerifyKey,
  options: VerifyOptions<L>,
): Promise<VerifyResult<L>> {
  const lanes: readonly SessionLane[] = options.lanes ?? DEFAULT_LANES;
  let payload;
  try {
    // jose's overloads split JWKS-resolver vs key; both are valid runtime args.
    const verified = await jwtVerify(jwt, key as JWTVerifyGetKey, {
      issuer: options.issuer,
      audience: options.audience,
      algorithms: ["RS256"],
    });
    payload = verified.payload;
  } catch (error) {
    return { ok: false, reason: classifyJoseError(error) };
  }

  const typ = payload.typ;
  if (!isSessionLane(typ) || !lanes.includes(typ)) {
    return { ok: false, reason: "wrong_typ" };
  }
  if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
    return { ok: false, reason: "no_exp" };
  }
  const exp = payload.exp;
  const issued =
    typeof payload.iat === "number" && Number.isFinite(payload.iat)
      ? { iat: payload.iat }
      : {};

  // The casts below are sound: `typ` was just checked against `lanes`, which
  // is `readonly L[]` (or the default when `L` is `user_session`), so the
  // session built in each branch is the `L` arm of `VerifiedSession`.
  if (typ === "reader_session") {
    const parsed = ConsumedReaderClaimsSchema.safeParse(payload);
    if (!parsed.success || parsed.data.sub !== parsed.data.readerId) {
      return { ok: false, reason: "malformed_claims" };
    }
    const method = ReaderMethodClaimSchema.safeParse(payload);
    const session: VerifiedSession = {
      lane: "reader_session",
      jwt,
      exp,
      ...issued,
      claims: {
        sub: parsed.data.sub,
        readerId: parsed.data.readerId,
        ...(parsed.data.linked === undefined
          ? {}
          : { linked: parsed.data.linked }),
        ...(method.success && method.data.method !== undefined
          ? { method: method.data.method }
          : {}),
      },
    };
    return { ok: true, ...session } as VerifyResult<L>;
  }

  const parsed = ConsumedClaimsSchema.safeParse(payload);
  if (!parsed.success) {
    return { ok: false, reason: "malformed_claims" };
  }
  // Built key by key rather than spread, so an absent claim stays absent
  // instead of becoming a key whose value is `undefined` — the shape callers
  // already log and compare against.
  const claims: SessionClaims = {};
  if (parsed.data.sub !== undefined) claims.sub = parsed.data.sub;
  if (parsed.data.readerId !== undefined)
    claims.readerId = parsed.data.readerId;
  const role = RoleClaimSchema.safeParse(payload);
  if (role.success && role.data.internalRole !== undefined)
    claims.internalRole = role.data.internalRole;
  const roster = RosterClaimSchema.safeParse(payload);
  if (roster.success && roster.data.readerNames !== undefined)
    claims.readerNames = roster.data.readerNames;
  const session: VerifiedSession = {
    lane: "user_session",
    jwt,
    exp,
    ...issued,
    claims,
  };
  return { ok: true, ...session } as VerifyResult<L>;
}
